import { ChatFab } from "@cohesive/ui-components";
import type { CSSProperties } from "react";

/**
 * The minimized floating chat: the canonical ChatKit FAB launcher, positioned
 * inside the surface's content region (not viewport-fixed) so it respects the
 * surrounding rail/pane layout. Clicking it opens the chat window; while closed
 * it pulses softly when a run is in flight. Shared shell for the canvas and
 * home/workspace surfaces — the thin per-surface wrappers pass placement.
 */
export function FloatingChatFab({
  onOpen,
  running,
  dockStyle,
  cornerClassName,
  "data-onboarding-target": onboardingTarget,
}: {
  onOpen: () => void;
  /** A run is in progress — pulse the launcher so activity isn't missed. */
  running: boolean;
  /**
   * When set, the launcher pins to the LEFT (viewport-fixed) instead of the
   * region's bottom-right — used while a full-width detail pane occupies the
   * right. The caller computes the geometry from the content-region rect.
   */
  dockStyle?: CSSProperties;
  /** Surface-specific classes for the normal corner placement. */
  cornerClassName: string;
  /** Onboarding anchor, a literal where the canvas passes it ("agent-chat-toggle"). */
  "data-onboarding-target"?: string;
}) {
  return (
    <div
      data-floating-chat="fab"
      data-onboarding-target={onboardingTarget}
      style={dockStyle}
      className={dockStyle ? "fixed z-50" : cornerClassName}
    >
      {running && (
        // Activity pulse rendered as an expanding box-shadow ring (see the
        // `fab-ping` keyframes) rather than an `animate-ping` transform. A
        // scaled overlay bleeds past this corner wrapper and enlarges the
        // surface's scroll area, flashing scrollbars when macOS "always show
        // scrollbars" is on; box-shadow is excluded from scrollable overflow,
        // so the pulse can grow freely without ever adding scrollbars.
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full motion-safe:animate-fab-ping"
        />
      )}
      {/* Neutralize ChatFab's own `fixed bottom-6 right-6 z-[1000]` so it sits in
          this shell-relative wrapper instead. Call `onOpen()` with no args — the
          button forwards its click event, which must not reach `expand()` (it
          would be mistaken for a session id). */}
      <ChatFab
        onClick={() => onOpen()}
        className="relative bottom-auto right-auto z-auto"
      />
    </div>
  );
}
